/**
 * Critical Safety Utility for Default_PC Prevention
 *
 * This module provides functionality to prevent "Default_PC" from being published
 * via R2F or GSLA operations, as specified by the user requirement:
 * "One important thing to prevent is we should never allow r2f or gsla to publish
 * for Default_PC that specific entry would cause lot's of issue"
 */

import { HierarchicalTableData, PendingChange } from '../components/hierarchicalTable/types';
import { PendingThresholdChange } from '../types/r2fTypes';
import { GSLADefinitionsByProductionCapability } from '../types/gslaApi';
import { GetFCMetadataResponse } from '../types/fcMetadataTypes';

export interface DefaultPCValidationResult {
    hasDefaultPC: boolean;
    filteredItems: any[];
    defaultPCCount: number;
    warningMessage?: string;
}

export interface DefaultPCSafetyBlockResult {
    shouldBlock: boolean;
    errorMessage: string;
    userMessage: string;
    defaultPCCount: number;
}

/**
 * The exact string that must be prevented from being published
 */
export const FORBIDDEN_PC_NAME = 'Default_PC';

/**
 * Validates and filters out any entries with "Default_PC" as production capability
 *
 * @param items - Array of items to validate (R2F changes, GSLA changes, etc.)
 * @param getProductionCapability - Function to extract PC name from each item
 * @returns Validation result with filtered items and warning information
 */
export function validateAndFilterDefaultPC<T>(
    items: T[],
    getProductionCapability: (item: T) => string
): DefaultPCValidationResult {
    if (!items || items.length === 0) {
        return {
            hasDefaultPC: false,
            filteredItems: [],
            defaultPCCount: 0
        };
    }

    const defaultPCItems: T[] = [];
    const safeItems: T[] = [];

    // Separate Default_PC items from safe items
    items.forEach(item => {
        const pcName = getProductionCapability(item);
        if (pcName === FORBIDDEN_PC_NAME) {
            defaultPCItems.push(item);
        } else {
            safeItems.push(item);
        }
    });

    const hasDefaultPC = defaultPCItems.length > 0;
    const warningMessage = hasDefaultPC
        ? `CRITICAL SAFETY WARNING: ${defaultPCItems.length} "Default_PC" entries were excluded from this operation to prevent system issues. Please review your changes and remove any "Default_PC" entries before saving.`
        : undefined;

    return {
        hasDefaultPC,
        filteredItems: safeItems,
        defaultPCCount: defaultPCItems.length,
        warningMessage
    };
}

/**
 * Consolidated validation function for both R2F and GSLA operations
 * Returns safety block information with consistent messaging
 */
export function validateForDefaultPCSafety(
    changes: any[],
    operationType: 'R2F' | 'GSLA',
    context: string = ''
): DefaultPCSafetyBlockResult {
    const validation = validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
    
    if (validation.hasDefaultPC) {
        const errorMessage = createDefaultPCBlockedMessage(operationType, validation.defaultPCCount);
        const userMessage = `🛡️ SAVE BLOCKED: ${validation.defaultPCCount} "${FORBIDDEN_PC_NAME}" entries were blocked for safety. Please remove all "${FORBIDDEN_PC_NAME}" entries and try again.`;
        
        return {
            shouldBlock: true,
            errorMessage,
            userMessage,
            defaultPCCount: validation.defaultPCCount
        };
    }
    
    return {
        shouldBlock: false,
        errorMessage: '',
        userMessage: '',
        defaultPCCount: 0
    };
}

/**
 * Validates R2F threshold changes for Default_PC
 */
export function validateR2FChangesForDefaultPC(changes: any[]): DefaultPCValidationResult {
    return validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
}

/**
 * Validates GSLA changes for Default_PC
 */
export function validateGSLAChangesForDefaultPC(changes: any[]): DefaultPCValidationResult {
    return validateAndFilterDefaultPC(changes, (change) => change.productionCapability || '');
}

/**
 * Validates R2F bulk publish requests for Default_PC
 */
export function validateR2FBulkRequestForDefaultPC(publishRequests: any[]): DefaultPCValidationResult {
    const allChanges: any[] = [];
    
    // Extract all production capabilities from the bulk request
    publishRequests.forEach(request => {
        if (request.r2fToPublish) {
            Object.keys(request.r2fToPublish).forEach(pcName => {
                allChanges.push({ productionCapability: pcName, warehouseId: request.warehouseId });
            });
        }
    });

    const validation = validateAndFilterDefaultPC(allChanges, (change) => change.productionCapability || '');
    
    if (validation.hasDefaultPC) {
        // Filter the original publish requests to remove Default_PC entries
        const filteredRequests = publishRequests.map(request => {
            if (!request.r2fToPublish) return request;
            
            const filteredR2FToPublish = { ...request.r2fToPublish };
            delete filteredR2FToPublish[FORBIDDEN_PC_NAME];
            
            return {
                ...request,
                r2fToPublish: filteredR2FToPublish
            };
        }).filter(request =>
            // Remove requests that have no production capabilities left after filtering
            request.r2fToPublish && Object.keys(request.r2fToPublish).length > 0
        );

        return {
            ...validation,
            filteredItems: filteredRequests
        };
    }

    return {
        ...validation,
        filteredItems: publishRequests
    };
}

/**
 * Creates a user-friendly error message when Default_PC is detected
 */
export function createDefaultPCBlockedMessage(operationType: 'R2F' | 'GSLA', count: number): string {
    return `❌ SAVE BLOCKED FOR SAFETY: Found ${count} "${FORBIDDEN_PC_NAME}" entr${count === 1 ? 'y' : 'ies'} in your ${operationType} changes.

"${FORBIDDEN_PC_NAME}" cannot be published as it would cause serious system issues.

Please remove all "${FORBIDDEN_PC_NAME}" entries from your changes and try saving again.`;
}

// ─── PSSD Validation Types ───────────────────────────────────────────────────

export interface PSSDBulkValidationResult {
    shouldBlock: boolean;
    errorMessage: string;
    rejectedRows: Array<{ rowIndex: number; productionCapability: string; warehouseId?: string }>;
}

export interface MinimumWHTValidationResult {
    shouldBlock: boolean;
    errorMessage: string;
    pcsMissingWHTs: string[];
}

export interface GSLAExistenceValidationResult {
    shouldBlock: boolean;
    errorMessage: string;
    missingGSLAEntries: Array<{ productionCapability: string; handlingTier: string }>;
    slaLink?: { href: string; label: string };
}

// ─── PSSD Constants ─────────────────────────────────────────────────────────

/** PC names forbidden for PSSD sites (compared case-insensitively) */
const PSSD_FORBIDDEN_PC_NAMES = ['default_pc', 'default_fc_pc'];

/** Shared empty result for passing validations */
const PSSD_BULK_PASS: Readonly<PSSDBulkValidationResult> = Object.freeze({ shouldBlock: false, errorMessage: '', rejectedRows: [] });
const MIN_WHT_PASS: Readonly<MinimumWHTValidationResult> = Object.freeze({ shouldBlock: false, errorMessage: '', pcsMissingWHTs: [] });
const GSLA_EXISTENCE_PASS: Readonly<GSLAExistenceValidationResult> = Object.freeze({ shouldBlock: false, errorMessage: '', missingGSLAEntries: [] });

/**
 * Returns true if the given production capability name is forbidden for PSSD sites.
 */
function isPSSDForbiddenPC(productionCapability: string): boolean {
    return PSSD_FORBIDDEN_PC_NAMES.includes((productionCapability || '').toLowerCase());
}

/**
 * Validates parsed CSV rows for DEFAULT_FC_PC on PSSD sites.
 * Case-insensitive match against "Default_PC" and "DEFAULT_FC_PC".
 * Returns passing result when isPSSD is false.
 */
export function validatePSSDDefaultPC(
    rows: Array<{ productionCapability: string }>,
    isPSSD: boolean,
): PSSDBulkValidationResult {
    if (!isPSSD || !rows || rows.length === 0) {
        return PSSD_BULK_PASS;
    }

    const rejectedRows: PSSDBulkValidationResult['rejectedRows'] = [];

    rows.forEach((row, index) => {
        if (isPSSDForbiddenPC(row.productionCapability)) {
            rejectedRows.push({ rowIndex: index, productionCapability: row.productionCapability });
        }
    });

    if (rejectedRows.length === 0) {
        return PSSD_BULK_PASS;
    }

    return {
        shouldBlock: true,
        errorMessage: `❌ UPLOAD BLOCKED: Found ${rejectedRows.length} DEFAULT_FC_PC row(s) in your CSV. DEFAULT_FC_PC rows are not permitted for PSSD sites. Please remove those rows and try again.`,
        rejectedRows,
    };
}

/**
 * Async variant for bulk uploads: determines which warehouses in the CSV are PSSD,
 * then validates rows for DEFAULT_FC_PC.
 *
 * @param rows - Parsed CSV rows with warehouseId and productionCapability
 * @param fetchFCMetadata - Function to fetch FC metadata for a warehouse (injected to avoid circular imports)
 */
export async function validatePSSDDefaultPCForBulkUpload(
    rows: Array<{ warehouseId?: string; productionCapability: string }>,
    fetchFCMetadata: (warehouseId: string) => Promise<GetFCMetadataResponse> | undefined,
): Promise<PSSDBulkValidationResult> {
    if (!rows || rows.length === 0) {
        return PSSD_BULK_PASS;
    }

    // Extract unique warehouse IDs (skip rows without warehouseId)
    const uniqueWarehouseIds = Array.from(new Set(rows.map(r => r.warehouseId).filter((id): id is string => !!id)));

    if (uniqueWarehouseIds.length === 0) {
        return PSSD_BULK_PASS;
    }

    // Fetch FC metadata for each warehouse to determine PSSD status
    const pssdWarehouses = new Set<string>();
    await Promise.all(
        uniqueWarehouseIds.map(async (whId) => {
            try {
                const metadata = await fetchFCMetadata(whId);
                if (metadata?.fcType === 'SSD') {
                    pssdWarehouses.add(whId);
                }
            }
            catch {
                // If metadata fetch fails, treat as non-PSSD (safe default)
            }
        }),
    );

    if (pssdWarehouses.size === 0) {
        return PSSD_BULK_PASS;
    }

    // Check rows for PSSD warehouses with DEFAULT_FC_PC
    const rejectedRows: PSSDBulkValidationResult['rejectedRows'] = [];
    rows.forEach((row, index) => {
        if (row.warehouseId && pssdWarehouses.has(row.warehouseId) && isPSSDForbiddenPC(row.productionCapability)) {
            rejectedRows.push({ rowIndex: index, productionCapability: row.productionCapability, warehouseId: row.warehouseId });
        }
    });

    if (rejectedRows.length === 0) {
        return PSSD_BULK_PASS;
    }

    const affectedSites = Array.from(new Set(rejectedRows.map(r => r.warehouseId).filter(Boolean))).join(', ');
    const affectedRowNums = rejectedRows.map(r => r.rowIndex + 1).join(', ');
    return {
        shouldBlock: true,
        errorMessage: `❌ UPLOAD BLOCKED: Found ${rejectedRows.length} DEFAULT_FC_PC row(s) for PSSD site(s): ${affectedSites}. Rows ${affectedRowNums} contain DEFAULT_FC_PC for these sites. Please remove those rows and try again.`,
        rejectedRows,
    };
}

/**
 * Validates that every PC being saved on a PSSD site has at least one WHT
 * with a GSLA value configured. Inspects the combination of current data
 * and pending changes to determine the effective WHT count per PC.
 * Returns passing result when isPSSD is false.
 */
export function validateMinimumWHTForPSSD(
    changes: PendingChange[],
    currentData: HierarchicalTableData | undefined,
    isPSSD: boolean,
): MinimumWHTValidationResult {
    if (!isPSSD || !currentData || !changes || changes.length === 0) {
        return MIN_WHT_PASS;
    }

    const pcsInChanges = new Set(changes.map(c => c.productionCapability));
    const pcsMissingWHTs: string[] = [];

    pcsInChanges.forEach(pcName => {
        const pcData = currentData.productionCapabilities.find(pc => pc.name === pcName);
        const pcChanges = changes.filter(c => c.productionCapability === pcName);

        // If the default tier (entire PC) is being deleted, skip — deletion is always allowed
        const defaultTierChange = pcChanges.find(c => c.isDefault);
        if (defaultTierChange && defaultTierChange.action === 'Delete') {
            return;
        }

        // If all changes for this PC are deletions, skip — removing tiers is always allowed
        const allDeletions = pcChanges.every(c => c.action === 'Delete');
        if (allDeletions) {
            return;
        }

        // Count WHTs that will have a GSLA value after applying changes
        let configuredWHTCount = 0;

        // Check default tier
        if (defaultTierChange) {
            if (defaultTierChange.newGSLA !== undefined && defaultTierChange.newGSLA > 0) {
                configuredWHTCount++;
            }
        }
        else if (pcData?.defaultHandlingTier?.currentGSLA !== undefined && pcData.defaultHandlingTier.currentGSLA > 0) {
            configuredWHTCount++;
        }

        // Check additional tiers (existing in data)
        const additionalTiers = pcData?.additionalHandlingTiers || [];
        additionalTiers.forEach(tier => {
            const tierChange = pcChanges.find(c => !c.isDefault && c.handlingTier === tier.name);
            if (tierChange) {
                if (tierChange.action !== 'Delete' && tierChange.newGSLA !== undefined && tierChange.newGSLA > 0) {
                    configuredWHTCount++;
                }
            }
            else if (tier.currentGSLA !== undefined && tier.currentGSLA > 0) {
                configuredWHTCount++;
            }
        });

        // Count new tiers being added that aren't in current data
        pcChanges.forEach(c => {
            if (!c.isDefault && c.action !== 'Delete' && c.newGSLA !== undefined && c.newGSLA > 0) {
                if (!additionalTiers.some(t => t.name === c.handlingTier)) {
                    configuredWHTCount++;
                }
            }
        });

        if (configuredWHTCount === 0) {
            pcsMissingWHTs.push(pcName);
        }
    });

    if (pcsMissingWHTs.length === 0) {
        return MIN_WHT_PASS;
    }

    const pcList = pcsMissingWHTs.join(', ');
    return {
        shouldBlock: true,
        errorMessage: `❌ SAVE BLOCKED: The following Production Capabilit${pcsMissingWHTs.length === 1 ? 'y' : 'ies'} must have at least one WHT with a GSLA value configured for PSSD sites: ${pcList}. Please configure at least one WHT before saving.`,
        pcsMissingWHTs,
    };
}

/**
 * Validates that a corresponding GSLA entry exists for each WHT-PC
 * combination being saved as a TRB threshold on a PSSD site.
 * Returns passing result when isPSSD is false.
 */
export function validateGSLAExistsForTRB(
    changes: PendingThresholdChange[],
    gslaData: GSLADefinitionsByProductionCapability | undefined,
    isPSSD: boolean,
    warehouseId?: string,
): GSLAExistenceValidationResult {
    if (!isPSSD || !changes || changes.length === 0) {
        return GSLA_EXISTENCE_PASS;
    }

    // Exclude deletions — deleting a threshold doesn't require GSLA
    const nonDeleteChanges = changes.filter(c => c.action !== 'Delete');
    if (nonDeleteChanges.length === 0) {
        return GSLA_EXISTENCE_PASS;
    }

    // Deduplicate by PC+WHT key to prevent duplicate entries in error messages
    const seen = new Set<string>();
    const uniqueChanges = nonDeleteChanges.filter(c => {
        const key = `${c.productionCapability}|${c.handlingTier}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });

    // If no GSLA data at all on a PSSD site, block everything
    if (!gslaData?.publishedGSLAList?.length) {
        return {
            shouldBlock: true,
            errorMessage: `❌ SAVE BLOCKED: No GSLA data found for this PSSD site. Please configure GSLA values before saving TRB thresholds.${warehouseId ? ' Go to the ' : ''}`,
            slaLink: warehouseId ? { href: `/sla/site/${warehouseId}`, label: 'SLA page' } : undefined,
            missingGSLAEntries: uniqueChanges.map(c => ({ productionCapability: c.productionCapability, handlingTier: c.handlingTier })),
        };
    }

    // Build a lookup set of existing GSLA entries: "PC|WHT"
    const gslaLookup = new Set<string>(
        gslaData.publishedGSLAList.map(def => `${def.productionCapability}|${def.warehouseHandlingTier || 'Default'}`),
    );

    // Check each unique non-delete change against the GSLA lookup
    const missingGSLAEntries = uniqueChanges
        .filter(c => !gslaLookup.has(`${c.productionCapability}|${c.handlingTier}`))
        .map(c => ({ productionCapability: c.productionCapability, handlingTier: c.handlingTier }));

    if (missingGSLAEntries.length === 0) {
        return GSLA_EXISTENCE_PASS;
    }

    const details = missingGSLAEntries.map(e => `${e.productionCapability} / ${e.handlingTier}`).join(', ');
    const gslaLink = warehouseId ? { href: `/sla/site/${warehouseId}`, label: 'SLA page' } : undefined;
    return {
        shouldBlock: true,
        errorMessage: `❌ SAVE BLOCKED: The following WHT-PC combinations do not have a corresponding GSLA entry. Please configure GSLA first: ${details}.${warehouseId ? ' Go to the ' : ''}`,
        slaLink: gslaLink,
        missingGSLAEntries,
    };
}

/**
 * Logs safety violations for audit purposes
 */
export function logDefaultPCSafetyViolation(
    operationType: 'R2F' | 'GSLA',
    userAlias: string,
    defaultPCCount: number,
    warehouseId?: string
): void {
    const logMessage = `DEFAULT_PC_SAFETY_VIOLATION: User ${userAlias} attempted to publish ${defaultPCCount} "${FORBIDDEN_PC_NAME}" entries via ${operationType}${warehouseId ? ` for warehouse ${warehouseId}` : ''}. Operation was blocked for safety.`;
    
    console.warn(logMessage);
    
    // In a production environment, this would also send to monitoring/alerting systems
    // Example: sendToMonitoring('SECURITY_VIOLATION', logMessage);
}